/**
 * `styles.*` wire contract (garment Increment 1, `garment.styles`).
 *
 * The Style File anchor. Quantities are plain integer pieces (a ratio grid
 * counts garments, not money); `fobPricePaise` crosses as stringified bigint
 * (`moneyPaise`) and is only set at costing-lock (Increment 2). The procedures
 * sit behind `entitledProcedureFor("garment.styles")` on the server.
 *
 * Garment-type keys are duplicated from `@flint/domain` GARMENT_TYPE_TEMPLATES
 * (api-contract may not import domain, per the §15 import direction); the domain
 * re-validates via `isGarmentType`.
 */
import { z } from "zod";
import { uuid, paged, isoDate, moneyPaise } from "../_common.js";

export const styleStatus = z.enum([
  "draft",
  "po_confirmed",
  "costing_locked",
  "pp_approved",
  "in_production",
  "shipped",
  "closed",
  "cancelled",
]);

/**
 * Garment-type key — relaxed from z.enum to z.string (#124) because the
 * catalog now holds 60+ types and maintaining two enums in sync is fragile.
 * The domain's `isGarmentType()` + `GarmentStyleUnknownTypeError` is the
 * authoritative gate; the wire schema only enforces shape.
 */
export const garmentType = z.string().trim().min(1).max(32);

const qtyInt = z.number().int().min(0).max(10_000_000);

export const ratioCellInput = z.object({
  colour: z.string().trim().min(1).max(40),
  colourHex: z.string().trim().max(7).nullable().optional(),
  size: z.string().trim().min(1).max(16),
  qty: qtyInt,
});

export const createInput = z.object({
  styleRef: z.string().trim().min(1).max(40),
  buyerId: uuid,
  garmentType,
  name: z.string().trim().min(1).max(160),
  currency: z.string().trim().length(3).optional(),
  poNumber: z.string().trim().max(40).nullable().optional(),
  poDate: isoDate.nullable().optional(),
  exFactoryDate: isoDate.nullable().optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
  lines: z.array(ratioCellInput).min(1).max(400),
});
export const createOutput = z.object({ id: uuid, jobNo: z.string() });

/** Path A (ADR-0044): the instant "Create style" button on an order's garment. */
export const createFromOrderItemInput = z.object({ itemId: uuid });
export const createFromOrderItemOutput = createOutput;

export const addPoDetailsInput = z.object({
  id: uuid,
  poNumber: z.string().trim().min(1).max(40),
  poDate: isoDate,
  exFactoryDate: isoDate.nullable().optional(),
  currency: z.string().trim().length(3).nullable().optional(),
  lines: z.array(ratioCellInput).min(1).max(400).optional(),
});
export const addPoDetailsOutput = z.object({ id: uuid });

export const transitionStatusInput = z.object({ id: uuid, status: styleStatus });
export const transitionStatusOutput = z.object({ id: uuid, status: styleStatus });

export const cloneInput = z.object({
  id: uuid,
  styleRef: z.string().trim().min(1).max(40),
  name: z.string().trim().min(1).max(160).nullable().optional(),
  exFactoryDate: isoDate.nullable().optional(),
});
export const cloneOutput = z.object({ id: uuid });

export const getInput = z.object({ id: uuid });

const ratioCell = z.object({
  id: uuid,
  position: z.number().int().positive(),
  colour: z.string(),
  colourHex: z.string().nullable(),
  size: z.string(),
  qty: z.number().int().nonnegative(),
});

const ratioGridSummary = z.object({
  totalQty: z.number().int().nonnegative(),
  colours: z.array(z.string()),
  sizes: z.array(z.string()),
  byColour: z.array(
    z.object({ colour: z.string(), colourHex: z.string().nullable(), qty: z.number().int() }),
  ),
  bySize: z.array(z.object({ size: z.string(), qty: z.number().int() })),
});

export const styleDetail = z.object({
  id: uuid,
  styleRef: z.string(),
  jobNo: z.string().nullable(),
  buyerId: uuid,
  buyerName: z.string(),
  buyerGstin: z.string().nullable(),
  buyerCity: z.string().nullable(),
  buyerState: z.string().nullable(),
  garmentType: z.string(),
  name: z.string(),
  status: styleStatus,
  currency: z.string(),
  poNumber: z.string().nullable(),
  poDate: isoDate.nullable(),
  exFactoryDate: isoDate.nullable(),
  fobPricePaise: moneyPaise.nullable(),
  techPackObjectKey: z.string().nullable(),
  imageObjectKey: z.string().nullable(),
  parentStyleId: uuid.nullable(),
  parentStyleRef: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
  createdByActorId: z.string(),
  createdByActorType: z.string(),
  lines: z.array(ratioCell),
  grid: ratioGridSummary,
  linkedOrder: z.object({ id: uuid, orderNo: z.string() }).nullable(),
});
export const getOutput = styleDetail;

export const listInput = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
  status: styleStatus.optional(),
  buyerId: uuid.optional(),
  garmentType: garmentType.optional(),
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
  search: z.string().trim().min(1).max(80).optional(),
  /** LinkStyleDialog (ADR-0044): a style is one production run for one order's garment. */
  excludeLinked: z.boolean().optional(),
});

export const listOutput = paged(
  z.object({
    id: uuid,
    styleRef: z.string(),
    name: z.string(),
    garmentType: z.string(),
    status: styleStatus,
    buyerId: uuid,
    buyerName: z.string(),
    buyerCity: z.string().nullable(),
    buyerState: z.string().nullable(),
    buyerCountry: z.string().nullable(),
    poNumber: z.string().nullable(),
    poDate: isoDate.nullable(),
    exFactoryDate: isoDate.nullable(),
    currency: z.string(),
    totalQty: z.number().int().nonnegative(),
    colourCount: z.number().int().nonnegative(),
    sizeCount: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
  }),
).extend({
  counts: z.object({
    all: z.number().int().nonnegative(),
    draft: z.number().int().nonnegative(),
    po_confirmed: z.number().int().nonnegative(),
    costing_locked: z.number().int().nonnegative(),
    pp_approved: z.number().int().nonnegative(),
    in_production: z.number().int().nonnegative(),
    shipped: z.number().int().nonnegative(),
    closed: z.number().int().nonnegative(),
    cancelled: z.number().int().nonnegative(),
  }),
  kpis: z.object({
    openCount: z.number().int().nonnegative(),
    openPieces: z.number().int().nonnegative(),
    exFactoryThisWeekCount: z.number().int().nonnegative(),
    draftCount: z.number().int().nonnegative(),
    topBuyerName: z.string().nullable(),
    topBuyerStyleCount: z.number().int().nonnegative(),
  }),
});

/** `styles.templates` — garment-type templates (key + label + category + sizes). */
export const templatesOutput = z.array(
  z.object({
    key: z.string(),
    label: z.string(),
    category: z.string(),
    sizes: z.array(z.string()),
    styleCodePrefix: z.string(),
  }),
);
